import { supabase } from '../lib/supabaseClient';

export interface ProductEngagementRecord {
  productId: string;
  productName: string;
  category: string | null;
  viewCount: number;
  addToCartCount: number;
  removeFromCartCount: number;
  checkoutStarts: number;
  orderCount: number;
  conversionRate: number;
}

export interface ProductEngagementSummary {
  topViewed: ProductEngagementRecord[];
  topNoPurchase: ProductEngagementRecord[];
}

const DELIVERED_STATUSES = new Set(['delivered', 'completed', 'fulfilled']);
const pickFirst = <T>(value: T | T[] | null | undefined): T | null =>
  Array.isArray(value) ? value[0] ?? null : value ?? null;

export async function fetchProductEngagement(
  rangeDays = 30,
  limit = 5
): Promise<ProductEngagementSummary> {
  const start = new Date();
  start.setUTCDate(start.getUTCDate() - (rangeDays - 1));
  start.setUTCHours(0, 0, 0, 0);
  const startIso = start.toISOString();

  const [interactionsResult, ordersResult] = await Promise.all([
    supabase
      .from('product_interactions')
      .select(
        `
          product_id,
          action,
          created_at,
          products (
            id,
            name,
            category
          )
        `
      )
      .gte('created_at', startIso),
    supabase
      .from('order_items')
      .select(
        `
          product_id,
          products (
            id,
            name,
            category
          ),
          orders:orders (
            status,
            created_at
          )
        `
      )
      .gte('orders.created_at', startIso),
  ]);

  if (interactionsResult.error) {
    throw interactionsResult.error;
  }
  if (ordersResult.error) {
    throw ordersResult.error;
  }

  const productMap = new Map<string, ProductEngagementRecord>();

  const ensureRecord = (productId?: string | null, name?: string | null, category?: string | null) => {
    if (!productId) {
      return null;
    }

    if (!productMap.has(productId)) {
      productMap.set(productId, {
        productId,
        productName: name ?? 'Bilinmeyen ürün',
        category: category ?? null,
        viewCount: 0,
        addToCartCount: 0,
        removeFromCartCount: 0,
        checkoutStarts: 0,
        orderCount: 0,
        conversionRate: 0,
      });
    }

    const record = productMap.get(productId)!;
    if (name && record.productName === 'Bilinmeyen ürün') {
      record.productName = name;
    }
    if (category && !record.category) {
      record.category = category;
    }
    return record;
  };

  (interactionsResult.data ?? []).forEach((row) => {
    const product = pickFirst(row.products);
    const record = ensureRecord(row.product_id, product?.name, product?.category);
    if (!record) {
      return;
    }

    switch ((row.action ?? '').toLowerCase()) {
      case 'view':
        record.viewCount += 1;
        break;
      case 'add_to_cart':
        record.addToCartCount += 1;
        break;
      case 'remove_from_cart':
        record.removeFromCartCount += 1;
        break;
      case 'start_checkout':
        record.checkoutStarts += 1;
        break;
      default:
        break;
    }
  });

  (ordersResult.data ?? []).forEach((row) => {
    const order = pickFirst(row.orders);
    const status = (order?.status ?? '').toLowerCase();
    if (!DELIVERED_STATUSES.has(status)) {
      return;
    }
    const product = pickFirst(row.products);
    const record = ensureRecord(row.product_id, product?.name, product?.category);
    if (!record) {
      return;
    }
    record.orderCount += 1;
  });

  const allRecords = Array.from(productMap.values()).map((record) => ({
    ...record,
    conversionRate: record.viewCount > 0 ? record.orderCount / record.viewCount : 0,
  }));

  const topViewed = [...allRecords]
    .sort((a, b) => b.viewCount - a.viewCount || b.orderCount - a.orderCount)
    .slice(0, limit);

  const topNoPurchase = allRecords
    .filter((record) => record.viewCount > 0 && record.orderCount === 0)
    .sort((a, b) => b.viewCount - a.viewCount)
    .slice(0, limit);

  return {
    topViewed,
    topNoPurchase,
  };
}
